import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import * as signalR from '@microsoft/signalr';
import { API_ENDPOINTS } from '../config';
import { getChatConnection, joinLobby, leaveLobby } from '../services/chatConnection';
import './Styles/Chat.css';

function parseJwt(token) {
  try {
    const payload = token.split('.')[1];
    const jsonPayload = decodeURIComponent(
      atob(payload)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return {};
  }
}

const getRawToken = () => {
  const t = localStorage.getItem('JWToken');
  if (!t) return null;
  return t.replace(/^Bearer\s+/i, '').trim();
};

const normalizeGuid = (v) => String(v ?? '').toLowerCase();

const getLobbyId = (lobby) => {
  return (
    lobby?.id ||
    lobby?.Id ||
    lobby?.lobbyId ||
    lobby?.LobbyId ||
    lobby?.lobbyID ||
    lobby?.LobbyID ||
    lobby?.lobby?.id ||
    lobby?.lobby?.Id ||
    lobby?.Lobby?.Id ||
    lobby?.Lobby?.id ||
    null
  );
};

const normalizeMessage = (m) => {
  if (!m) return null;
  return {
    id: m.id ?? m.Id ?? `${Date.now()}-${Math.random()}`,
    senderUserId: m.senderUserId ?? m.SenderUserId ?? m.senderId ?? m.SenderId ?? null,
    senderName: m.senderName ?? m.SenderName ?? '',
    text: m.text ?? m.Text ?? m.message ?? m.Message ?? '',
    sentAt: m.sentAt ?? m.SentAt ?? m.time ?? m.Time ?? null
  };
};

const formatTime = (dt) => {
  if (!dt) return '';
  try {
    return new Date(dt).toLocaleTimeString();
  } catch {
    return String(dt);
  }
};

export default function LobbyChat({ invite, lobby, targetUserId, isMobileOverlay }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [isConnected, setIsConnected] = useState(false);

  const isMounted = useRef(false);
  const seenIds = useRef(new Set());
  const messagesDivRef = useRef(null);

  const rawToken = getRawToken();
  const payload = useMemo(() => (rawToken ? parseJwt(rawToken) : {}), [rawToken]);

  const lobbyId = useMemo(() => getLobbyId(lobby), [
    lobby?.id,
    lobby?.Id,
    lobby?.lobbyId,
    lobby?.LobbyId,
    lobby?.lobbyID,
    lobby?.LobbyID,
    lobby?.lobby?.id,
    lobby?.lobby?.Id,
    lobby?.Lobby?.Id,
    lobby?.Lobby?.id
  ]);

  const intendedLobby = !!invite;
  const isLobbyMode = intendedLobby || !!lobbyId;
  const isPrivateMode = !!targetUserId;

  const modeKey = isPrivateMode
    ? `private:${normalizeGuid(targetUserId)}`
    : isLobbyMode
      ? `lobby:${normalizeGuid(lobbyId || 'pending')}:${String(invite || '')}`
      : 'global';

  const getDisplayName = (msg) => {
    if (!msg) return 'System';
    if (msg.senderName) return msg.senderName;

    const myNames = [payload.name, payload.unique_name, payload.preferred_username, payload.sub]
      .filter(Boolean)
      .map(String);

    if (msg.senderUserId && payload.sub && String(msg.senderUserId) === String(payload.sub)) {
      return myNames[0] || payload.sub;
    }

    return msg.senderUserId ? String(msg.senderUserId) : 'System';
  };

  const isMyMessage = (msg) => {
    if (!msg?.senderUserId) return false;
    if (!payload?.sub) return false;
    return String(msg.senderUserId) === String(payload.sub);
  };

  const messageClass = (msg) => {
    if (!msg?.senderUserId && !msg?.senderName) return 'chat-message chat-message--system';
    return isMyMessage(msg) ? 'chat-message chat-message--me' : 'chat-message chat-message--other';
  };

  const clearMessages = () => {
    seenIds.current = new Set();
    setMessages([]);
  };

  const loadHistory = async () => {
    const t = getRawToken();
    if (!t) return;

    if (intendedLobby && !lobbyId) {
      return;
    }

    const headers = { Authorization: `Bearer ${t}`, Accept: 'application/json' };
    const currentUserId = payload?.sub;

    let url;
    if (isPrivateMode) {
      if (!currentUserId || !targetUserId) return;
      url = API_ENDPOINTS.CHAT_PRIVATE_HISTORY(currentUserId, targetUserId);
    } else if (lobbyId) {
      url = API_ENDPOINTS.CHAT_LOBBY_HISTORY(lobbyId);
    } else {
      url = API_ENDPOINTS.CHAT_GLOBAL_HISTORY;
    }

    const res = await fetch(url, { method: 'GET', headers });

    if (res.status === 204) {
      if (isMounted.current) {
        clearMessages();
      }
      return;
    }

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || 'Chat history failed');
    }

    const data = await res.json();
    const list = Array.isArray(data) ? data.map(normalizeMessage).filter(Boolean) : [];

    const nextSeen = new Set();
    for (const msg of list) nextSeen.add(String(msg.id).toLowerCase());

    if (isMounted.current) {
      seenIds.current = nextSeen;
      setMessages(list);
    }
  };

  useEffect(() => {
    isMounted.current = true;

    let conn = null;

    clearMessages();
    setError('');

    const onReceiveGlobalMessage = (m) => {
      if (!isMounted.current) return;
      if (isLobbyMode || isPrivateMode) return;

      const msg = normalizeMessage(m);
      if (!msg) return;

      const key = String(msg.id).toLowerCase();
      if (seenIds.current.has(key)) return;

      seenIds.current.add(key);
      setMessages(prev => [...prev, msg]);
    };

    const onReceiveLobbyMessage = (incomingLobbyId, m) => {
      if (!isMounted.current) return;
      if (!lobbyId) return;
      if (normalizeGuid(incomingLobbyId) !== normalizeGuid(lobbyId)) return;

      const msg = normalizeMessage(m);
      if (!msg) return;

      const key = String(msg.id).toLowerCase();
      if (seenIds.current.has(key)) return;

      seenIds.current.add(key);
      setMessages(prev => [...prev, msg]);
    };

    const onReceivePrivateMessage = (fromUserId, msg) => {
      if (!isMounted.current) return;

      let currentFromUserId;
      let currentMessage;

      if (fromUserId !== undefined && msg !== undefined) {
        currentFromUserId = fromUserId;
        currentMessage = msg;
      } else if (fromUserId && typeof fromUserId === 'object') {
        currentMessage = fromUserId;
        currentFromUserId = currentMessage.senderUserId || currentMessage.SenderUserId;
      } else {
        return;
      }

      const normalizedFrom = normalizeGuid(currentFromUserId);
      const normalizedTarget = normalizeGuid(targetUserId);
      const myUserId = payload?.sub ? normalizeGuid(payload.sub) : null;

      if (!isPrivateMode) {
        return;
      }

      if (normalizedTarget && normalizedFrom !== normalizedTarget && normalizedFrom !== myUserId) {
        return;
      }

      const message = normalizeMessage(currentMessage);
      if (!message) return;

      const key = String(message.id).toLowerCase();
      if (seenIds.current.has(key)) return;

      seenIds.current.add(key);
      setMessages(prev => [...prev, message]);
    };

    const init = async () => {
      if (!rawToken) {
        setError('No token');
        setIsConnected(false);
        return;
      }

      if (intendedLobby && !lobbyId) {
        setIsConnected(false);
        return;
      }

      try {
        conn = await getChatConnection();

        if (!isLobbyMode && !isPrivateMode) {
          conn.on('ReceiveGlobalMessage', onReceiveGlobalMessage);
        }

        if (lobbyId) {
          conn.on('ReceiveLobbyMessage', onReceiveLobbyMessage);
        }

        if (isPrivateMode) {
          conn.on('ReceivePrivateMessage', onReceivePrivateMessage);
        }

        setIsConnected(conn.state === signalR.HubConnectionState.Connected);

        if (lobbyId) {
          await joinLobby(lobbyId);
        }

        await loadHistory();
      } catch (e) {
        setIsConnected(false);
        setError(e?.message || 'Chat init failed');
      }
    };

    init();

    return () => {
      isMounted.current = false;

      if (conn) {
        try { conn.off('ReceiveGlobalMessage', onReceiveGlobalMessage); } catch {}
        try { conn.off('ReceiveLobbyMessage', onReceiveLobbyMessage); } catch {}
        try { conn.off('ReceivePrivateMessage', onReceivePrivateMessage); } catch {}
      }

      if (lobbyId) {
        leaveLobby(lobbyId).catch(() => {});
      }
    };
  }, [modeKey]);

  useEffect(() => {
    if (messagesDivRef.current) {
      messagesDivRef.current.scrollTop = messagesDivRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;

    try {
      const conn = await getChatConnection();

      if (isPrivateMode && targetUserId) {
        await conn.invoke('SendPrivateMessage', targetUserId, trimmed);
      } else if (lobbyId) {
        await conn.invoke('SendLobbyMessage', lobbyId, trimmed);
      } else {
        await conn.invoke('SendGlobalMessage', trimmed);
      }

      setText('');
      setError('');
    } catch (e) {
      setError('Send failed: ' + (e?.message || 'unknown error'));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const uiTitle = targetUserId ? 'PRIVATE CHAT' : invite ? `LOBBY (${invite})` : 'GLOBAL CHAT';
  const statusClass = isConnected ? 'chat-status chat-status--connected' : 'chat-status chat-status--connecting';
  const statusText = isConnected ? 'ONLINE' : 'OFFLINE';

  const inputDisabled = !rawToken || (intendedLobby && !lobbyId) || !isConnected;

  return (
    <Card className="shadow chat-card" style={{ height: '100%', margin: 4 }}>
      <Card.Body className="chat-body">
        <div className="chat-header">
          <div className="chat-header-left">
            <div className="chat-title">{uiTitle}</div>
          </div>
          <div className={statusClass}>{statusText}</div>
        </div>

        {error ? <div className="chat-error">{error}</div> : null}

        <div className="chat-area">
          {intendedLobby && !lobbyId ? (
            <div ref={messagesDivRef} className="chat-messages">
              <div className="chat-message chat-message--system">
                Loading lobby... (lobbyId not available yet)
              </div>
            </div>
          ) : (
            <div ref={messagesDivRef} className="chat-messages">
              {messages.length === 0 ? (
                <div className="chat-message chat-message--system">No messages yet</div>
              ) : (
                messages.map(m => {
                  const isSystem = (!m.senderUserId && (!m.senderName || m.senderName === 'System'));
                  return (
                    <div key={m.id} className={messageClass(m)}>
                      {!isSystem ? (
                        <div className="chat-message-meta">
                          <span className="chat-message-who">{getDisplayName(m)}</span>
                          <span className="chat-message-time">{formatTime(m.sentAt)}</span>
                        </div>
                      ) : null}
                      <div className="chat-message-text">{m.text}</div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          <div className="chat-input-row">
            <input
              id="lobbyMessageInput"
              className="message-input"
              autoComplete="off"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={`Type ${isPrivateMode ? 'private' : invite ? 'lobby' : 'global'} message...`}
              disabled={inputDisabled}
              inputMode="text"
              enterKeyHint="send"
              onTouchStart={
                isMobileOverlay
                  ? (e) => {
                      e.stopPropagation();
                      e.currentTarget.focus();
                    }
                  : undefined
              }
              onPointerDown={
                isMobileOverlay
                  ? (e) => {
                      e.stopPropagation();
                    }
                  : undefined
              }
              style={{
                overflow: 'hidden',
                ...(isMobileOverlay
                  ? {
                      fontSize: '16px',
                      pointerEvents: 'auto',
                      touchAction: 'manipulation'
                    }
                  : {})
              }}
            />

            <button
              className="send-button send-button--square"
              onClick={sendMessage}
              disabled={!text.trim() || inputDisabled}
            >
              <i className="bi bi-arrow-left"></i>
            </button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
